import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { settingsApi } from '../api'
import { settingsErrorMessage } from '../errors'
import type { ConnectionTest, VideoDraft, VideoKind, VideoSettings } from '../types'

function editable(settings: VideoSettings): VideoDraft {
  return {
    revision: settings.revision,
    engine: { ...settings.engine, api_key: '' },
    exchange: { ...settings.exchange, secret_access_key: '' },
  }
}

export type VideoSettingsState = ReturnType<typeof useVideoSettings>

/**
 * Admin → Video engine (PRD-0074 SC-003): the saved settings as a draft with blank secrets (blank
 * keeps the saved one), a connection test per part, and a save. Running analyses keep the
 * revision they started with, which the saved message says.
 */
export function useVideoSettings() {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<VideoDraft | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [failed, setFailed] = useState(false)
  const [testingKind, setTestingKind] = useState<VideoKind | null>(null)
  const [testResults, setTestResults] = useState<Record<VideoKind, ConnectionTest | null>>({
    engine: null,
    exchange: null,
  })
  const report = useCallback((error: unknown) => {
    setFailed(true)
    setMessage(settingsErrorMessage(error, 'Video settings request failed.'))
  }, [])

  useEffect(() => {
    let mounted = true
    settingsApi
      .getVideo()
      .then(settings => {
        if (mounted) setDraft(editable(settings))
      })
      .catch(error => {
        if (mounted) report(error)
      })
    return () => {
      mounted = false
    }
  }, [report])

  const update = <K extends VideoKind>(kind: K, part: VideoDraft[K]) => {
    setDraft(current => (current ? { ...current, [kind]: part } : current))
    setTestResults(current => ({ ...current, [kind]: null }))
  }

  const test = async (kind: VideoKind) => {
    if (!draft) return
    setTestingKind(kind)
    setTestResults(current => ({ ...current, [kind]: null }))
    try {
      const result = await settingsApi.testVideo(draft, kind)
      setTestResults(current => ({ ...current, [kind]: result }))
    } catch (error) {
      setTestResults(current => ({
        ...current,
        [kind]: { ok: false, message: settingsErrorMessage(error, 'Connection test failed.') },
      }))
    } finally {
      setTestingKind(null)
    }
  }

  const save = async () => {
    if (!draft) return
    setSaving(true)
    setFailed(false)
    setMessage('')
    try {
      setDraft(editable(await settingsApi.saveVideo(draft)))
      setMessage(t('settings.video.saved'))
    } catch (error) {
      report(error)
    } finally {
      setSaving(false)
    }
  }

  return { draft, update, saving, message, failed, testingKind, testResults, test, save }
}
